import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { BrowserRouter, Route, Routes } from "react-router-dom"

import ScrollToTop from "@/components/ScrollToTop"
import AuthCallback from "@/components/auth/AuthCallback"
import PrivateRoute from "@/components/auth/PrivateRoute"
import { Toaster as Sonner } from "@/components/ui/sonner"
import { Toaster } from "@/components/ui/toaster"
import { TooltipProvider } from "@/components/ui/tooltip"
import { ApplicationProvider } from "@/contexts/ApplicationContext"
import { SessionProvider } from "@/contexts/SessionContext"

import ErrorBoundary from "./components/ErrorBoundary"
import ApplicationError from "./components/application/ApplicationError"
import StageRestrictedRoute from "./components/application/StageRestrictedRoute"
import { ActiveJobProvider } from "./contexts/ActiveJobContext"
import ApplicationCompletePage from "./pages/ApplicationCompletePage"
import ApplicationRejectedPage from "./pages/ApplicationRejectedPage"
import ApplicationStatusPage from "./pages/ApplicationStatusPage"
import AssessmentFailedPage from "./pages/AssessmentFailedPage"
import BasicDetailsPage from "./pages/BasicDetailsPage"
import ContractPage from "./pages/ContractPage"
import DocumentsPage from "./pages/DocumentsPage"
import DriverOnroadAssessmentPage from "./pages/DriverOnroadAssessmentPage"
import EmailConfirmationPage from "./pages/EmailConfirmationPage"
import HoldPage from "./pages/HoldPage"
import LandingPage from "./pages/LandingPage"
import LoginPage from "./pages/LoginPage"
import NotFound from "./pages/NotFound"
import OrientationConfirmationPage from "./pages/OrientationConfirmationPage"
import OrientationPage from "./pages/OrientationPage"
import OrientationQuizPage from "./pages/OrientationQuizPage"
import PendingPage from "./pages/PendingPage"
import PersonalDetailsPage from "./pages/PersonalDetailsPage"
import ScreeningPage from "./pages/ScreeningPage"
import TrialPage from "./pages/TrialPage"
import VehicleCollectionConfirmationPage from "./pages/VehicleCollectionConfirmationPage"
import VehicleCollectionPage from "./pages/VehicleCollectionPage"

const queryClient = new QueryClient()

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <ApplicationProvider>
          <ActiveJobProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ScrollToTop />
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/basic-details" element={<BasicDetailsPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/auth-callback" element={<AuthCallback />} />
                  <Route path="/application-error" element={<ApplicationError />} />
                  <Route path="/email-confirmation" element={<EmailConfirmationPage />} />
                  {/* Protected Routes */}
                  <Route element={<PrivateRoute />}>
                    {/* Stage-restricted routes */}
                    <Route path="/assessment-failed" element={<AssessmentFailedPage />} />
                    <Route
                      path="/driver-onroad-test"
                      element={
                        <StageRestrictedRoute>
                          <DriverOnroadAssessmentPage />
                        </StageRestrictedRoute>
                      }
                    />
                    <Route
                      path="/documents"
                      element={
                        <StageRestrictedRoute>
                          <DocumentsPage />
                        </StageRestrictedRoute>
                      }
                    />
                    <Route
                      path="/orientation"
                      element={
                        <StageRestrictedRoute>
                          <OrientationPage />
                        </StageRestrictedRoute>
                      }
                    />
                    <Route
                      path="/orientation-quiz"
                      element={
                        <StageRestrictedRoute>
                          <OrientationQuizPage />
                        </StageRestrictedRoute>
                      }
                    />
                    <Route
                      path="/screening"
                      element={
                        <StageRestrictedRoute>
                          <ScreeningPage />
                        </StageRestrictedRoute>
                      }
                    />
                    <Route
                      path="/orientation-confirmation"
                      element={<OrientationConfirmationPage />}
                    />
                    <Route
                      path="/personal-details"
                      element={
                        <StageRestrictedRoute>
                          <PersonalDetailsPage />
                        </StageRestrictedRoute>
                      }
                    />
                    <Route
                      path="/contract"
                      element={
                        <StageRestrictedRoute>
                          <ContractPage />
                        </StageRestrictedRoute>
                      }
                    />
                    <Route
                      path="/complete"
                      element={
                        <StageRestrictedRoute>
                          <ApplicationCompletePage />
                        </StageRestrictedRoute>
                      }
                    />
                    <Route
                      path="/application-status"
                      element={
                        <StageRestrictedRoute>
                          <ApplicationStatusPage />
                        </StageRestrictedRoute>
                      }
                    />
                    <Route
                      path="/rejected"
                      element={
                        <StageRestrictedRoute>
                          <ApplicationRejectedPage />
                        </StageRestrictedRoute>
                      }
                    />
                    <Route
                      path="/trial"
                      element={
                        <StageRestrictedRoute>
                          <TrialPage />
                        </StageRestrictedRoute>
                      }
                    />
                    <Route
                      path="/hold"
                      element={
                        <StageRestrictedRoute>
                          <HoldPage />
                        </StageRestrictedRoute>
                      }
                    />
                    <Route
                      path="/pending"
                      element={
                        <StageRestrictedRoute>
                          <PendingPage />
                        </StageRestrictedRoute>
                      }
                    />
                    <Route
                      path="/vehicle-collection"
                      element={
                        <StageRestrictedRoute>
                          <VehicleCollectionPage />
                        </StageRestrictedRoute>
                      }
                    />
                    <Route
                      path="/vehicle-collection-confirmation"
                      element={<VehicleCollectionConfirmationPage />}
                    />
                  </Route>

                  {/* 404 Route */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
            </TooltipProvider>
          </ActiveJobProvider>
        </ApplicationProvider>
      </SessionProvider>
    </QueryClientProvider>
  </ErrorBoundary>
)

export default App
